import { THEME_DEFAULT } from '@tinderapp/ui-core';
import { isEmpty } from 'lodash';

import { emptyArray, emptyObject } from 'Lib/constants/placeholder';

import {
  CTA,
  FullScreenInsendioModalV2,
  FullScreenModalV2CampaignData,
  InsendioModal,
  MediaType,
  ObsidianAppearance,
  ObsidianTokenType,
  PartialScreenInsendioModalV2,
  PartialScreenModalV2CampaignData,
  Token,
} from '../types';
import { MODAL_TYPE_FULL_SCREEN_V2, MODAL_TYPE_PARTIAL_SCREEN_V2 } from './common';

export const isFullScreenModalV2 = (
  modal: InsendioModal | FullScreenInsendioModalV2 | PartialScreenInsendioModalV2
): modal is FullScreenInsendioModalV2 => modal.template?.name === MODAL_TYPE_FULL_SCREEN_V2;

export const isPartialScreenModalV2 = (
  modal: InsendioModal | PartialScreenInsendioModalV2
): modal is PartialScreenInsendioModalV2 => modal.template?.name === MODAL_TYPE_PARTIAL_SCREEN_V2;

export const getFullScreenModalV2CampaignData = (data: FullScreenInsendioModalV2) => {
  const template = data?.template || emptyObject;

  // container
  const backgroundColor =
    template?.background && 'color' in template?.background ? template?.background.color : null;
  const backgroundImage =
    template?.background && 'media' in template?.background ? template?.background.media : null;

  // hero
  const hero = template?.hero ?? emptyObject;

  // body
  const header = template?.header ?? null;
  const body = template?.body ?? emptyArray;

  // cta
  const primaryCta = template?.primaryCta ?? emptyArray;
  const secondaryCta = template?.secondaryCta ?? emptyArray;

  // close
  const close = template?.close?.color ?? emptyObject;

  // presentation rule
  const presentationRule = data?.presentation?.rule ?? '';

  return {
    backgroundColor,
    backgroundImage,
    primaryCta,
    secondaryCta,
    header,
    hero,
    body,
    close,
    presentationRule,
  };
};

export const getPartialScreenModalV2CampaignData = (data: PartialScreenInsendioModalV2) => {
  const template = data?.template || emptyObject;

  // hero
  const hero = template?.hero ?? null;

  // body
  const header = template?.header ?? null;
  const description = template?.description ?? null;

  // cta
  const primaryCta = template?.primaryCta ?? null;
  const secondaryCta = template?.secondaryCta ?? null;

  // presentation rule
  const presentationRule = data?.presentation?.rule ?? '';

  return {
    primaryCta,
    secondaryCta,
    header,
    hero,
    description,
    presentationRule,
  };
};

export const getValidModal = (
  modalData: (InsendioModal | FullScreenInsendioModalV2 | PartialScreenInsendioModalV2)[]
):
  | InsendioModal
  | FullScreenInsendioModalV2
  | PartialScreenInsendioModalV2
  | typeof emptyObject => {
  if (isEmpty(modalData)) {
    return emptyObject;
  }
  const validModal = modalData.find((modal) => {
    if (!isFullScreenModalV2(modal) && !isPartialScreenModalV2(modal)) {
      return true;
    }
    if (isFullScreenModalV2(modal)) {
      const campaignData = getFullScreenModalV2CampaignData(modal);
      return validateFullScreenModalV2Campaign(campaignData);
    }
    if (isPartialScreenModalV2(modal)) {
      const campaignData = getPartialScreenModalV2CampaignData(modal);
      return validatePartialScreenModalV2Campaign(campaignData);
    }

    return false;
  });
  return validModal || emptyObject;
};

export const validateFullScreenModalV2Campaign = (data: FullScreenModalV2CampaignData) => {
  // Header and Hero are optional
  // There should either be a background color or a background image
  const { backgroundColor, backgroundImage, body, header, hero, primaryCta, secondaryCta, close } =
    data;

  const isValidBackground = !!backgroundColor || !!backgroundImage;
  const isValidBackgroundImage =
    !backgroundImage ||
    (validateMediaType(backgroundImage?.light) && validateMediaType(backgroundImage?.dark));

  const isValidHero = !hero || (validateMediaType(hero?.light) && validateMediaType(hero?.dark));

  const isValidHeader = !header || !!header?.body;
  const isValidBody =
    Array.isArray(body) && !isEmpty(body) && body.every((message) => !!message?.body);

  const isValidPrimaryCta = validateCta(primaryCta);
  const isValidSecondaryCta = validateCta(secondaryCta);

  const isValidClose = !!close;

  const areTokensValid = validateFullScreenModalV2CampaignTokens(data);

  return (
    isValidBackground &&
    isValidBackgroundImage &&
    isValidBody &&
    isValidHeader &&
    isValidHero &&
    isValidPrimaryCta &&
    isValidSecondaryCta &&
    isValidClose &&
    areTokensValid
  );
};

export const validateFullScreenModalV2CampaignTokens = ({
  backgroundColor,
  primaryCta,
  secondaryCta,
  header,
  body,
  close,
}: FullScreenModalV2CampaignData) => {
  const isValidBackgroundColor = !backgroundColor || validateToken(backgroundColor);
  const isValidPrimaryCta = validateCtaTokenSafely(primaryCta?.obsidianAppearance);
  const isValidSecondaryCta = validateCtaTokenSafely(secondaryCta?.obsidianAppearance);
  const isValidHeader =
    !header ||
    (validateToken(header?.obsidianAppearance?.fontStyle) &&
      validateToken(header?.obsidianAppearance?.foreground));
  const isValidMessage =
    !isEmpty(body) &&
    body.every(
      (message) =>
        validateToken(message?.obsidianAppearance?.fontStyle) &&
        validateToken(message?.obsidianAppearance?.foreground)
    );
  const isValidClose = validateTokenSafely(close);

  return (
    isValidBackgroundColor &&
    isValidPrimaryCta &&
    isValidSecondaryCta &&
    isValidHeader &&
    isValidMessage &&
    isValidClose
  );
};

export const validatePartialScreenModalV2Campaign = (data: PartialScreenModalV2CampaignData) => {
  // Hero and Secondary CTA are optional
  const { primaryCta, secondaryCta, header, description, hero } = data;

  const isValidHero = !hero || (validateMediaType(hero?.light) && validateMediaType(hero?.dark));

  const isValidHeader = !!header?.body;
  const isValidDescription = !!description?.body;

  const isValidPrimaryCta = validateCta(primaryCta);
  const isValidSecondaryCta = !secondaryCta || validateCta(secondaryCta);

  const areTokensValid = validatePartialScreenModalV2CampaignTokens(data);

  return (
    isValidHero &&
    isValidHeader &&
    isValidDescription &&
    isValidPrimaryCta &&
    isValidSecondaryCta &&
    areTokensValid
  );
};

export const validatePartialScreenModalV2CampaignTokens = ({
  primaryCta,
  secondaryCta,
  header,
  description,
}: PartialScreenModalV2CampaignData) => {
  const isValidPrimaryCta = validateCtaTokenSafely(primaryCta?.obsidianAppearance);
  const isValidSecondaryCta = !secondaryCta || validateCtaTokens(secondaryCta?.obsidianAppearance);
  const isValidHeader =
    validateTokenSafely(header?.obsidianAppearance?.fontStyle) &&
    validateTokenSafely(header?.obsidianAppearance?.foreground);

  const isValidDescription =
    validateTokenSafely(description?.obsidianAppearance?.fontStyle) &&
    validateTokenSafely(description?.obsidianAppearance?.foreground);

  return isValidPrimaryCta && isValidSecondaryCta && isValidHeader && isValidDescription;
};

export const validateCtaTokens = (tokens: ObsidianAppearance | null | undefined) => {
  if (!tokens) {
    return false;
  }
  return (
    validateToken(tokens.background) &&
    validateToken(tokens.foreground) &&
    validateToken(tokens.border) &&
    validateToken(tokens.overlay)
  );
};

export const validateToken = (token: Token | undefined | null) => {
  if (!token) {
    return false;
  }
  switch (token.type) {
    case ObsidianTokenType.Color:
      return !!token.name && !!THEME_DEFAULT.color[token.name];
    case ObsidianTokenType.Gradient:
      return !!token.name && !!THEME_DEFAULT.gradient[token.name];
    case ObsidianTokenType.Typography:
      return !!token.name && !!THEME_DEFAULT.fontFamily[token.name];
    default:
      return false;
  }
};

const validateTokenSafely = (token: Token | undefined | null): boolean =>
  validateToken(token) ?? false;

const validateCtaTokenSafely = (tokens: ObsidianAppearance | undefined | null): boolean =>
  validateCtaTokens(tokens) ?? false;

const validateCta = (cta: CTA | undefined | null): boolean =>
  !!cta?.body && !!cta?.action?.interaction;

const validateMediaType = (media: MediaType | undefined | null): boolean =>
  !!media?.url && !!media?.mimeType && !!media?.metadata;
